<template>
  <section class="min-h-[calc(100vh-7rem)] bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
    <div class="mx-auto grid max-w-5xl gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
      <form class="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm sm:p-6" @submit.prevent="submitPassword">
        <div class="border-b border-slate-200 pb-5">
          <div class="flex items-start gap-3">
            <HistoryBackButton />
            <div class="min-w-0">
              <p class="text-xs font-medium uppercase tracking-[0.18em] text-slate-400">Account security</p>
              <h1 class="mt-2 text-3xl font-semibold text-slate-950">Change password</h1>
              <p class="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
                Use a strong password that is unique to your NetFlyr account.
              </p>
            </div>
          </div>
        </div>

        <div class="mt-6 grid gap-5">
          <PasswordField
            v-model="currentPassword"
            label="Current password"
            placeholder="Enter current password"
            autocomplete="current-password"
            :visible="showCurrentPassword"
            @toggle="showCurrentPassword = !showCurrentPassword"
          />

          <PasswordField
            v-model="newPassword"
            label="New password"
            placeholder="Create a new password"
            autocomplete="new-password"
            :visible="showNewPassword"
            generator
            @toggle="showNewPassword = !showNewPassword"
            @generate="generatePassword"
          />

          <div class="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <div class="flex items-center justify-between gap-3 text-xs font-medium uppercase tracking-[0.16em] text-slate-500">
              <span>Password strength</span>
              <span :class="strengthLabelClass">{{ strengthLabel }}</span>
            </div>
            <div class="mt-3 h-2 overflow-hidden rounded-full bg-slate-200">
              <div class="h-full rounded-full transition-all" :class="strengthBarClass" :style="{ width: strengthPercent + '%' }" />
            </div>
            <ul class="mt-3 grid gap-2 text-xs font-medium leading-5 text-slate-600">
              <li
                v-for="item in passwordChecklist"
                :key="item.label"
                class="flex items-center gap-2"
                :class="item.valid ? 'text-emerald-700' : 'text-slate-500'"
              >
                <span
                  class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[0.65rem] font-medium"
                  :class="item.valid ? 'bg-emerald-100 text-emerald-700' : 'bg-white text-slate-400'"
                >
                  {{ item.valid ? 'OK' : '-' }}
                </span>
                {{ item.label }}
              </li>
            </ul>
          </div>

          <PasswordField
            v-model="confirmPassword"
            label="Confirm new password"
            placeholder="Re-enter new password"
            autocomplete="new-password"
            :visible="showConfirmPassword"
            :error="Boolean(confirmPassword && !passwordsMatch)"
            @toggle="showConfirmPassword = !showConfirmPassword"
          />

          <p v-if="formError" class="rounded-2xl bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            {{ formError }}
          </p>

          <div class="flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:justify-end">
            <button
              class="inline-flex min-h-11 items-center justify-center rounded-full border border-slate-200 bg-white px-5 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
              type="button"
              @click="router.push('/account')"
            >
              Cancel
            </button>
            <button
              class="nf-primary-button"
              type="submit"
              :disabled="isSubmitting"
            >
              {{ isSubmitting ? 'Updating...' : 'Update password' }}
            </button>
          </div>
        </div>
      </form>

      <aside class="self-start rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-sm lg:sticky lg:top-24">
        <div class="grid h-12 w-12 place-items-center rounded-2xl bg-blue-50 text-blue-700">
          <svg class="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <rect x="4" y="10" width="16" height="10" rx="2" />
            <path d="M8 10V7a4 4 0 0 1 8 0v3" />
            <path d="M12 14v2" />
          </svg>
        </div>
        <h2 class="mt-4 text-xl font-semibold text-slate-950">Password rules</h2>
        <p class="mt-2 text-sm leading-6 text-slate-500">
          Generate a strong password or create one manually. The strength meter updates as you type.
        </p>
        <div class="mt-5 grid gap-3">
          <div v-for="item in passwordChecklist" :key="`summary-${item.label}`" class="flex items-center gap-3 rounded-2xl bg-slate-50 px-3 py-2">
            <span class="grid h-7 w-7 place-items-center rounded-full text-xs font-medium" :class="item.valid ? 'bg-emerald-100 text-emerald-700' : 'bg-white text-slate-400'">
              {{ item.valid ? 'OK' : '-' }}
            </span>
            <span class="text-sm font-medium text-slate-600">{{ item.label }}</span>
          </div>
        </div>
      </aside>
    </div>
  </section>
</template>

<script setup>
import { computed, defineComponent, h, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useHead } from '@vueuse/head'
import Swal from 'sweetalert2'
import HistoryBackButton from '@/components/common/HistoryBackButton.vue'
import api from '@/services/api'
import { useSessionStore } from '@/stores/session'

useHead({
  title: 'Change Password | NetFlyr',
  meta: [
    { name: 'description', content: 'Change your NetFlyr password securely to protect your account, posted flyers, saved boards, chats, profile details, and local community activity with stronger credentials.' },
    { name: 'keywords', content: 'NetFlyr change password, update password, account security, password settings, secure account, login security' }
  ]
})

const router = useRouter()
const session = useSessionStore()
const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')
const showCurrentPassword = ref(false)
const showNewPassword = ref(false)
const showConfirmPassword = ref(false)
const formError = ref('')
const isSubmitting = ref(false)

const passwordChecks = computed(() => ({
  length: newPassword.value.length >= 12,
  uppercase: /[A-Z]/.test(newPassword.value),
  lowercase: /[a-z]/.test(newPassword.value),
  number: /\d/.test(newPassword.value),
  symbol: /[^A-Za-z0-9]/.test(newPassword.value)
}))

const passwordChecklist = computed(() => [
  { label: 'Use at least 12 characters', valid: passwordChecks.value.length },
  { label: 'Include uppercase and lowercase letters', valid: passwordChecks.value.uppercase && passwordChecks.value.lowercase },
  { label: 'Add at least one number', valid: passwordChecks.value.number },
  { label: 'Add at least one symbol', valid: passwordChecks.value.symbol }
])

const strengthScore = computed(() => Object.values(passwordChecks.value).filter(Boolean).length)
const strengthPercent = computed(() => (strengthScore.value / 5) * 100)
const passwordsMatch = computed(() => Boolean(newPassword.value && confirmPassword.value && newPassword.value === confirmPassword.value))

const getApiMessage = (payload, fallback) =>
  payload?.message || payload?.msg || payload?.errors?.[0]?.msg || fallback

const isSuccessResponse = (payload) => {
  const status = String(payload?.status ?? '').trim().toLowerCase()
  return !status || ['ok', 'success', 'true', '1'].includes(status)
}

const strengthLabel = computed(() => {
  if (!newPassword.value) return 'Start'
  if (strengthScore.value <= 2) return 'Weak'
  if (strengthScore.value <= 4) return 'Good'
  return 'Strong'
})

const strengthBarClass = computed(() => {
  if (!newPassword.value) return 'bg-slate-300'
  if (strengthScore.value <= 2) return 'bg-red-500'
  if (strengthScore.value <= 4) return 'bg-amber-500'
  return 'bg-emerald-500'
})

const strengthLabelClass = computed(() => {
  if (!newPassword.value) return 'text-slate-500'
  if (strengthScore.value <= 2) return 'text-red-600'
  if (strengthScore.value <= 4) return 'text-amber-600'
  return 'text-emerald-700'
})

const generatePassword = () => {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const lower = 'abcdefghijkmnopqrstuvwxyz'
  const numbers = '23456789'
  const symbols = '!@#$%^&*?'
  const groups = [upper, lower, numbers, symbols]
  const allCharacters = groups.join('')
  const generated = [
    ...groups.map((group) => group[Math.floor(Math.random() * group.length)]),
    ...Array.from({ length: 12 }, () => allCharacters[Math.floor(Math.random() * allCharacters.length)])
  ]
    .sort(() => Math.random() - 0.5)
    .join('')

  newPassword.value = generated
  confirmPassword.value = generated
  showNewPassword.value = true
  showConfirmPassword.value = true
  formError.value = ''
}

const submitPassword = async () => {
  formError.value = ''

  if (!currentPassword.value || !newPassword.value || !confirmPassword.value) {
    formError.value = 'Fill all password fields.'
    return
  }

  if (!passwordsMatch.value) {
    formError.value = 'Confirm password must match your new password.'
    return
  }

  if (strengthScore.value < 5) {
    formError.value = 'Please use a strong password before updating.'
    return
  }

  isSubmitting.value = true

  try {
    const id = session.userId || session.user?.user_id || ''
    if (!id) throw new Error('Sign in again before changing your password.')

    const { data } = await api.changePassword({
      id,
      oldpwd: currentPassword.value,
      newpwd: newPassword.value
    })

    if (!isSuccessResponse(data)) {
      throw new Error(getApiMessage(data, 'Password could not be updated.'))
    }

    await Swal.fire({
      icon: 'success',
      title: 'Password updated',
      text: 'Your password has been changed.',
      confirmButtonColor: '#2563eb'
    })

    router.push('/account')
  } catch (error) {
    formError.value = getApiMessage(error?.response?.data, error?.message || 'Password could not be updated.')
  } finally {
    isSubmitting.value = false
  }
}

const EyeIcon = defineComponent({
  props: { off: { type: Boolean, default: false } },
  setup(props) {
    return () =>
      props.off
        ? h('svg', { class: 'h-5 w-5', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'aria-hidden': 'true' }, [
            h('path', { d: 'M10.7 5.1A10.9 10.9 0 0 1 12 5c5 0 9 4.5 10 7a13.5 13.5 0 0 1-3.2 4.5' }),
            h('path', { d: 'M6.6 6.6A13 13 0 0 0 2 12c1 2.5 5 7 10 7a10.9 10.9 0 0 0 5.4-1.5' }),
            h('path', { d: 'M2 2l20 20' })
          ])
        : h('svg', { class: 'h-5 w-5', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'aria-hidden': 'true' }, [
            h('path', { d: 'M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Z' }),
            h('circle', { cx: '12', cy: '12', r: '3' })
          ])
  }
})

const GeneratorIcon = defineComponent({
  setup() {
    return () =>
      h('svg', { class: 'h-5 w-5', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'aria-hidden': 'true' }, [
        h('path', { d: 'M15 4V2' }),
        h('path', { d: 'M15 16v-2' }),
        h('path', { d: 'M8 9h2' }),
        h('path', { d: 'M20 9h2' }),
        h('path', { d: 'm17.8 6.2 1.4-1.4' }),
        h('path', { d: 'm12.2 11.8-1.4 1.4' }),
        h('path', { d: 'M3 21l9-9' }),
        h('path', { d: 'M12 12 9 9' })
      ])
  }
})

const PasswordField = defineComponent({
  props: {
    modelValue: { type: String, default: '' },
    label: { type: String, required: true },
    placeholder: { type: String, default: '' },
    autocomplete: { type: String, default: 'off' },
    visible: { type: Boolean, default: false },
    generator: { type: Boolean, default: false },
    error: { type: Boolean, default: false }
  },
  emits: ['update:modelValue', 'toggle', 'generate'],
  setup(props, { emit }) {
    return () =>
      h('label', { class: 'grid gap-1.5 text-sm font-medium text-slate-700' }, [
        h('span', props.label),
        h('div', { class: 'relative' }, [
          h('input', {
            class: [
              'min-h-12 w-full rounded-2xl border bg-white px-4 py-3 font-normal text-slate-950 outline-none transition placeholder:text-slate-400 focus:ring-4',
              props.generator ? 'pr-20' : 'pr-12',
              props.error ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : 'border-slate-300 focus:border-blue-500 focus:ring-blue-100'
            ],
            value: props.modelValue,
            type: props.visible ? 'text' : 'password',
            autocomplete: props.autocomplete,
            placeholder: props.placeholder,
            onInput: (event) => emit('update:modelValue', event.target.value)
          }),
          props.generator
            ? h('button', {
                class: 'absolute right-11 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-4 focus:ring-blue-100',
                type: 'button',
                'aria-label': 'Generate strong password',
                onClick: () => emit('generate')
              }, [h(GeneratorIcon)])
            : null,
          h('button', {
            class: 'absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus:ring-4 focus:ring-blue-100',
            type: 'button',
            'aria-label': props.visible ? 'Hide password' : 'Show password',
            'aria-pressed': props.visible,
            onClick: () => emit('toggle')
          }, [h(EyeIcon, { off: props.visible })])
        ])
      ])
  }
})
</script>
